import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLinkToken } from '../use-link-token';
import { z } from 'zod';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { PasswordInput } from '@/components/form/password-input';
import { useZodForm } from '@/components/form/use-zod-form';
import { LoadingState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ROLE_LABELS,
  acceptInvitation,
  getInvitationPreview,
} from '@/features/organization/api/organization';
import { AuthCard } from '../components/auth-card';
import { MIN_PASSWORD_LENGTH, newPassword } from '../schemas';

export function AcceptInvitationPage() {
  const token = useLinkToken();
  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => getInvitationPreview(token),
    retry: false,
  });
  const [done, setDone] = useState(false);

  if (preview.isPending) {
    return (
      <AuthCard title="Convite">
        <LoadingState variant="block" />
      </AuthCard>
    );
  }

  if (preview.isError) {
    return (
      <AuthCard title="Convite indisponível">
        <p className="text-sm">{preview.error.message}</p>
      </AuthCard>
    );
  }

  if (done) {
    return (
      <AuthCard
        title="Convite aceito"
        description={`Você agora faz parte de ${preview.data.organizationName}.`}
      >
        <Button asChild className="w-full">
          <Link to="/entrar">Entrar</Link>
        </Button>
      </AuthCard>
    );
  }

  const { accountExists, organizationName, email, role } = preview.data;
  return (
    <AuthCard
      title={`Convite para ${organizationName}`}
      description={`${email} · ${ROLE_LABELS[role]}`}
    >
      {accountExists ? (
        <ExistingAccountForm token={token} onDone={() => setDone(true)} />
      ) : (
        <NewAccountForm token={token} onDone={() => setDone(true)} />
      )}
    </AuthCard>
  );
}

const existingSchema = z.object({ password: z.string().min(1, 'Informe a sua senha.') });

/** Quem já tem conta confirma com a senha de sempre para entrar na empresa nova. */
function ExistingAccountForm({ token, onDone }: { token: string; onDone: () => void }) {
  const form = useZodForm(existingSchema, { password: '' });
  const [serverError, setServerError] = useState<unknown>(null);
  const onSubmit = form.handleSubmit(async ({ password }) => {
    setServerError(null);
    try {
      await acceptInvitation(token, { password });
      onDone();
    } catch (caught) {
      setServerError(caught);
    }
  });
  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <p className="text-sm text-muted-foreground">
        Você já tem conta no troig. Confirme com a sua senha para entrar nesta empresa.
      </p>
      <FormError error={serverError} fallback="Não foi possível aceitar o convite." />
      <Field control={form.control} name="password" label="Sua senha" required>
        {(field) => <PasswordInput {...field} autoComplete="current-password" />}
      </Field>
      <SubmitButton submitting={form.formState.isSubmitting} />
    </form>
  );
}

const newSchema = z.object({
  name: z.string().trim().min(2, 'Informe o seu nome.'),
  password: newPassword,
});

/** Conta nova: nome e a primeira senha. */
function NewAccountForm({ token, onDone }: { token: string; onDone: () => void }) {
  const form = useZodForm(newSchema, { name: '', password: '' });
  const [serverError, setServerError] = useState<unknown>(null);
  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await acceptInvitation(token, values);
      onDone();
    } catch (caught) {
      setServerError(caught);
    }
  });
  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <FormError error={serverError} fallback="Não foi possível aceitar o convite." />
      <Field control={form.control} name="name" label="Seu nome" required>
        {(field) => <Input {...field} autoComplete="name" />}
      </Field>
      <Field
        control={form.control}
        name="password"
        label="Crie uma senha"
        required
        hint={`Mínimo de ${MIN_PASSWORD_LENGTH} caracteres.`}
      >
        {(field) => <PasswordInput {...field} autoComplete="new-password" />}
      </Field>
      <SubmitButton submitting={form.formState.isSubmitting} />
    </form>
  );
}

function SubmitButton({ submitting }: { submitting: boolean }) {
  return (
    <Button type="submit" className="w-full" disabled={submitting}>
      {submitting ? 'Aceitando…' : 'Aceitar convite'}
    </Button>
  );
}
